import React, { useEffect, useRef, useCallback } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";

// Idle auto-logout: logs the user out after 1 hour of inactivity.
// Set this to false to switch it off.
const ENABLE_IDLE_LOGOUT = true;

const IDLE_TIMEOUT_MS = 10 * 60 * 60 * 1000
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

const PrivateLayout = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", {
      replace: true,
      state: { error: "You were logged out due to inactivity." },
    });
  }, [navigate]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(logout, IDLE_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!ENABLE_IDLE_LOGOUT) return; // idle logout disabled
    if (!token) return; // no session, nothing to time out

    resetTimer();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer));

    // If the tab was backgrounded/asleep and comes back, re-check activity
    const onVisibility = () => {
      if (document.visibilityState === "visible") resetTimer();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ❌ Not logged in → block everything
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // ✅ Logged in → allow access
  return <Outlet />;
};

export default PrivateLayout;